/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  ContactDetailsDialogConfig,
  contactDetailsDialogRoute,
  FormActionPipelines,
  FormFlowState,
  JobDetailsModel,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ContactType,
  SalesFolderStatus,
  SystemRoleBit,
  systemRolesBitSet,
  UserStatus,
} from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  EnumDictionary,
  getRouteInitData,
  TextDictionary,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService, translationTuple } from '@gh/core-mls';
import {
  AddressAutocompleteComponent,
  AddressComponents,
  AutocompleteLoadMoreEvent,
  DialogService,
  foldAddressComponentsForXero,
  getControlValue,
} from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';

import { assign, range } from 'lodash';
import { Observable } from 'rxjs';

import {
  ContactListItem,
  JobDetailsTabBusinessService,
  JobGeneralTabInfo,
  JobStageListItem,
} from '../../../resources/job-details-tab';
import { JobDetailsGeneralTabFormState } from './job-details-general-tab-form.service';

@Component({
  standalone: false,
  selector: 'gh-job-details-general-tab',
  templateUrl: 'job-details-general-tab.component.html',
  styleUrls: ['job-details-general-tab.component.scss'],
  providers: [JobDetailsGeneralTabFormState],
})
export class JobDetailsGeneralTabComponent extends AbstractFormComponent<JobGeneralTabInfo> implements OnInit, AfterViewInit {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  contractTypes: TextDictionary;
  notProceedingReasons: any[];
  statusDict: EnumDictionary<SalesFolderStatus>;
  generalInfo: JobGeneralTabInfo;
  months = range(1, 13);
  jobStages: JobStageListItem[];
  jobId: ID;
  referredBy: string = '';
  showManualAddressFields = false;

  get canOpenSalesFolder(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Master) ||
      this.userContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT) ||
      this.userContext.hasPermission(BuilderPermission.Sales.Leads.VIEW);
  }

  get statusFieldValue(): string {
    let result = this.statusDict.get(this.form.get('salesFolderStatus')?.value);
    if (this.form.get('salesFolderStatus')?.value === SalesFolderStatus.WillNotComplete
      && this.form.get('salesFolderNotProceedingReason')?.value) {
      result += ' (' + this.notProceedingReasons.find((item) => `${item.id}`
        === `${this.form.get('salesFolderNotProceedingReason')?.value}`)?.name + ')';
    }
    return result;
  }

  get unitsCountValue(): number {
    const unitsCount = this.generalInfo.unitsCount;
    return unitsCount ? unitsCount : 1;
  }

  constructor(public form: JobDetailsGeneralTabFormState,
    private route: ActivatedRoute,
    private router: AppRouter,
    private businessService: JobDetailsTabBusinessService,
    private userContext: BuilderUserContext,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    public navigationService: BuilderNavigationService,
    private dialogService: DialogService,
    private translationService: TranslationService,
    private model: JobDetailsModel) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<JobGeneralTabInfo>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<JobGeneralTabInfo>) =>
        this.businessService.updateGeneralTabInfo({
          ...this.form.submitValue,
          version: this.generalInfo.version,
        })),
      finish: (state: FormFlowState<JobGeneralTabInfo>) => {
        const generalInfo = state.value;
        assign(this, { generalInfo });
        this.form.build(generalInfo);
        this.form.updateFinanceCompany();
      },
    });
  }

  get isCurrentSupervisorInactive(): boolean {
    const user = getControlValue(this.form.group, 'supervisor');
    if (!user) {
      return false;
    }

    const userStatus = user.status;
    return userStatus ? this.isSupervisorInactive(userStatus) : !this.generalInfo.isSupervisorActive;
  }

  get isCurrentJobAdminInactive(): boolean {
    const user = getControlValue(this.form.group, 'jobAdmin');
    if (!user) {
      return false;
    }

    const userStatus = user.status;
    return userStatus ? this.isJobAdminInactive(userStatus) : !this.generalInfo.isJobAdminActive;
  }

  ngOnInit(): void {
    const { generalInfo, contractTypes, jobId, notProceedingReasons, statusDict } = getRouteInitData(this.route);
    assign(this, { generalInfo, contractTypes, jobId });
    this.referredBy = generalInfo.referredBy ? generalInfo.referredBy.localization : '';
    this.jobStages = generalInfo.jobStages;
    this.notProceedingReasons = notProceedingReasons;
    this.statusDict = statusDict;
    this.form.build(generalInfo);
    this.form.updateFinanceCompany();

    const hasAddressData = !!(this.form.group.get('siteSuburb')?.value ||
      this.form.group.get('siteStreetNumber')?.value ||
      this.form.group.get('siteStreet')?.value ||
      this.form.group.get('sitePostcode')?.value);
    this.showManualAddressFields = hasAddressData;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.onAddressChange();
    });
  }

  loadContacts($event: AutocompleteLoadMoreEvent, types?: ContactType[]): void {
    $event.load(this.businessService.getContacts($event.query, types));
  }

  loadEstimatorUsers($event: AutocompleteLoadMoreEvent): void {
    this.loadUsers($event, SystemRoleBit.Estimator);
  }

  loadSupervisorUsers($event: AutocompleteLoadMoreEvent): void {
    this.loadUsers($event, SystemRoleBit.Supervisor);
  }

  loadJobAdminUsers($event: AutocompleteLoadMoreEvent): void {
    this.loadUsers($event, SystemRoleBit.JobAdmin);
  }

  loadUsers($event: AutocompleteLoadMoreEvent, bit: SystemRoleBit): void {
    const { query, load } = $event;
    const fullQuery = {
      ...query,
      filter: {
        name: query.filter,
        showInactive: false,
        systemRoles: systemRolesBitSet(bit),
      },
    };

    load(this.businessService.getUsers(fullQuery));
  }

  isSupervisorInactive(status: UserStatus): boolean {
    return UserStatus.Inactive === status;
  }

  isJobAdminInactive(status: UserStatus): boolean {
    return UserStatus.Inactive === status;
  }

  onFinanceCompanyChange(): void {
    this.form.updateFinanceCompany();
  }

  onCreateCounty(): void {
    this.openCounty();
  }

  onOpenCounty(): void {
    this.openCounty(this.form.getCounty());
  }

  onCreateFinanceCompany(): void {
    this.openFinanceCompany();
  }

  onOpenFinanceCompany(): void {
    this.openFinanceCompany(this.form.getFinanceCompany());
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  onReassign(): void {
    this.businessService.getGeneralTabInfo()
      .subscribe((info) => this.form.setSalesperson(info.salespersonName));
  }

  onOpenSalesFolderInfo(): void {
    this.router.navigateToChild([`sales/leads`, this.model.basicInfo.contactId], {
      parentTitle: `${this.translationService.get('lbl_job')}: #${this.model.jobNumber}`,
      queryParams: {
        salesFolderId: this.generalInfo.salesFolderId,
      },
    });
  }

  onAddressChange() {
    const streetNumber = this.form.group.get('siteStreetNumber')?.value || '';
    const street = this.form.group.get('siteStreet')?.value || '';
    const city = this.form.group.get('siteSuburb')?.value || '';
    const postCode = this.form.group.get('sitePostcode')?.value || '';

    const addressParts: string[] = [];
    if (streetNumber) addressParts.push(streetNumber);
    if (street) addressParts.push(street);
    if (city) addressParts.push(city);
    if (postCode) addressParts.push(postCode);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    if (!components) return;

    const c = foldAddressComponentsForXero(components);

    this.form.group.patchValue({
      siteStreetNumber: c.streetNo || '',
      siteStreet: c.street || '',
      siteSuburb: c.city || '',
      sitePostcode: c.postalCode || ''
    });

    ['siteStreetNumber', 'siteStreet', 'siteSuburb', 'sitePostcode'].forEach(field => {
      const control = this.form.group.get(field);
      if (control) control.markAsDirty();
    });
  }

  private openCounty(openedCounty?: ContactListItem): void {
    this.openContact(ContactType.CityCounty, openedCounty)
      .subscribe((county) => this.form.setCounty(county));
  }

  private openFinanceCompany(openedFinanceCompany?: ContactListItem): void {
    this.openContact(ContactType.FinanceCompany, openedFinanceCompany)
      .subscribe((financeCompany) => this.form.setFinanceCompany(financeCompany));
  }

  private openContact(contactType: ContactType, openedContact?: ContactListItem): Observable<ContactListItem> {
    return this.dialogService.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType,
      contactId: openedContact ? openedContact.id : void 0,
      parentTitle: this.translationService.get(
        'lbl_job_number_with_param',
        { number: this.model.basicInfo.jobNumber.toString() },
      ),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(({ contactId, name }) => (<ContactListItem>{ id: contactId, name })));
  }
}
